import type { en } from "./en";

/**
 * German.
 *
 * Typed against the English catalogue, so a missing key is a compile error
 * rather than a screen that reads half in one language and half in another.
 *
 * The register is deliberate: **Sie** throughout. The reader is a teacher or a
 * studio owner at work, and a German institution expects to be addressed
 * formally by a tool it pays for. The app the students hold uses "du"; this
 * does not.
 */
export const de: Record<keyof typeof en, string> = {
  // ── Rahmen ────────────────────────────────────────────────────────
  "app.name": "Spontar Backstage",
  "nav.dashboard": "Übersicht",
  "nav.people": "Personen",
  "nav.groups": "Gruppen",
  "nav.devices": "Geräte",
  "nav.composer": "Composer",
  "devices.title": "Geräte",
  "devices.intro": "Die Geräte der Teilnehmenden, die Ihrer Einrichtung gehören. Jedes wird hier einmal eingetragen, wenn die Kiste geöffnet wird. Ein Gerät hat kein Konto und meldet sich nie an — es wird an die Person im Raum gegeben, und die Lehrkraft gibt ihm eine Rolle in der Stunde.",
  "devices.enrol": "Gerät von Hand eintragen",
  "devices.enrolHelp": "Für ein Gerät, das nicht an diesen Computer angeschlossen werden kann. Alles unten steht auf dem Gerät; hier wird nichts installiert.",
  "devices.none": "Noch keine Geräte eingetragen",
  "devices.noneBody":
    "Geräte werden meist per USB über die Seite Geräte hinzufügen eingetragen, sobald ein Kit besteht. Ein eingetragenes Gerät wird zu jeder Stunde dieser Einrichtung zugelassen.",
  "devices.column.label": "Beschriftung",
  "devices.column.id": "Gerät",
  "devices.column.enrolled": "Eingetragen",
  "devices.column.state": "Status",
  "devices.column.actions": "Aktionen",
  "devices.inService": "Im Einsatz",
  "devices.notSeen": "Seit über {days} Tagen nicht gesehen",
  "devices.withdraw": "Zurückziehen",
  "devices.withdrawnOn": "Zurückgezogen am {date}",
  "devices.field.label": "Beschriftung",
  "devices.field.labelHint": "Was auf dem Gehäuse steht, damit eine Lehrkraft die Zeile dem Gerät in der Hand zuordnen kann.",
  "devices.field.id": "Gerätenummer",
  "devices.field.key": "Schlüssel",
  "devices.field.keyHint": "Der lange Code neben der Gerätenummer.",

  // ── Geräte: Hinzufügen über USB (K2) ─────────────────────────────
  "devices.column.kit": "Kit",
  "devices.noKit": "—",
  "devices.add.link": "Geräte hinzufügen",
  "devices.add.title": "Geräte hinzufügen",
  "devices.add.intro": "Schließen Sie ein Gerät mit einem USB-Kabel an diesen Computer an, wählen Sie das Kit, zu dem es gehört, und drücken Sie den Knopf. Das Gerät bekommt die aktuelle Software, das WLAN seines Kits und eine Beschriftung — Sie tippen nichts.",
  "devices.add.browser": "Diese Seite braucht Chrome oder Edge auf einem Laptop oder Desktop-Computer — das sind die Browser, die mit einem USB-Kabel sprechen können. Öffnen Sie sie dort und schließen Sie das Gerät an.",
  "devices.add.back": "Zurück zu den Geräten",
  "devices.add.noKits": "Noch keine Kits",
  "devices.add.noKitsBody": "Ein Gerät gehört zu einem Kit — der Kiste mit dem Router, mit dem es sich verbindet. Legen Sie zuerst das Kit an.",
  "devices.add.kit": "Kit",
  "devices.add.kitHint": "Das Gerät findet nur das WLAN dieses Kits. Seine Beschriftung beginnt mit dem Namen des Kits.",
  "devices.add.firmware": "Software-Version {version} wird auf das Gerät geschrieben.",
  "devices.add.firmwareUnavailable": "Die Gerätesoftware konnte von dieser Seite nicht geladen werden. Laden Sie die Seite neu; wenn es weiter fehlschlägt, sagen Sie Spontar Bescheid.",
  "devices.add.retry": "Erneut versuchen",
  "devices.add.resume": "Weiter, ohne die Software neu zu schreiben",
  "devices.add.startOver": "Software noch einmal schreiben",
  "devices.add.resumeHint": "Die Software ist bereits auf diesem Gerät. Ziehen Sie es ab, stecken Sie es wieder ein und machen Sie weiter — sie muss kein zweites Mal geschrieben werden.",
  "devices.add.start": "Angeschlossenes Gerät hinzufügen",
  "devices.add.steps": "Schritte",
  "devices.add.step.flash": "Software schreiben",
  "devices.add.step.network": "WLAN des Kits übergeben",
  "devices.add.step.identity": "Gerätenummer auslesen",
  "devices.add.step.enrol": "Eintragen",
  "devices.add.state.pending": "Wartet",
  "devices.add.state.running": "Läuft…",
  "devices.add.state.writing": "Schreibt, {percent} %",
  "devices.add.state.done": "Fertig",
  "devices.add.state.failed": "Fehlgeschlagen",
  "devices.add.screenDark": "Dieses Gerät ist registriert, aber sein Bildschirm ist nicht angegangen — in einer Stunde ist es damit nicht zu gebrauchen. Schließen Sie es an und fügen Sie es mit der neuesten Software noch einmal hinzu; geht es dann immer noch nicht an, ist es eine Platine, die diese Software nicht kennt.",
  "devices.add.done": "{label} ist bereit. Ziehen Sie das Gerät ab, schreiben Sie die Beschriftung auf das Gehäuse und schließen Sie das nächste an.",
  "devices.add.count": "{count} heute zu {kit} hinzugefügt.",
  "devices.add.error.port": "Das Gerät konnte nicht geöffnet werden. Ziehen Sie es ab, stecken Sie es wieder an und versuchen Sie es noch einmal.",
  "devices.add.error.portBusy": "Ein anderes Programm benutzt das Gerät. Schließen Sie es — einen seriellen Monitor, einen anderen Tab — und versuchen Sie es erneut.",
  "devices.add.error.noResponse": "Das Gerät hat nicht geantwortet. Versuchen Sie ein anderes Kabel oder einen anderen USB-Anschluss; manche Kabel laden nur. Wenn es weiter nicht antwortet, halten Sie die Reset-Taste des Geräts gedrückt, während Sie hier den Knopf drücken.",
  "devices.add.error.wrongBoard": "Das ist nicht die Art von Gerät, die diese Seite einrichten kann.",
  "devices.add.error.firmwareMissing": "Die Gerätesoftware ist auf dieser Seite noch nicht veröffentlicht. Sagen Sie Spontar Bescheid.",
  "devices.add.error.flash": "Die Software konnte nicht geschrieben werden. Ziehen Sie das Gerät ab, stecken Sie es wieder an und versuchen Sie es noch einmal.",
  "devices.add.error.noImprov": "Das Gerät ist nach dem Schreiben der Software nicht gestartet. Ziehen Sie es ab, stecken Sie es wieder an und versuchen Sie es noch einmal.",
  "devices.add.error.networkNotSeen": "Das Gerät hört kein Netz namens {ssid}. Schalten Sie den Router des Kits daneben ein und warten Sie eine Minute — und prüfen Sie, ob der Router auf den WLAN-Namen und das Passwort des Kits eingestellt ist, auf 2,4 GHz. Was das Gerät hört: {seen}",
  "devices.add.error.networkNotSeenNothing": "Das Gerät hört überhaupt kein WLAN, auch {ssid} nicht. Schalten Sie den Router des Kits daneben ein und warten Sie eine Minute.",
  "devices.add.error.networkRefused": "Das Gerät hört {ssid}, wurde aber nicht zugelassen. Das ist fast immer das Passwort — vergeben Sie auf der Kits-Seite ein neues und stellen Sie den Router darauf ein. Ein Netz, das nur 5 GHz anbietet, führt ebenfalls dazu; diese Geräte funken auf 2,4 GHz.",
  "devices.add.error.networkUnreachable": "Das Gerät hat das WLAN des Kits nicht erreicht. Schalten Sie den Router des Kits in der Nähe ein, warten Sie eine Minute und versuchen Sie es erneut.",
  "devices.add.error.network": "Das Gerät hat das WLAN nicht übernommen. Versuchen Sie es erneut; wenn es weiter fehlschlägt, sagen Sie Spontar Bescheid.",
  "devices.add.error.identity": "Das Gerät hat nicht gesagt, wer es ist. Ziehen Sie es ab, stecken Sie es wieder an und versuchen Sie es noch einmal.",
  "devices.add.error.enrol": "Das Gerät konnte nicht eingetragen werden. Prüfen Sie Ihre Verbindung und versuchen Sie es erneut.",

  // ── Kits ──────────────────────────────────────────────────────────
  "nav.kits": "Kits",
  "kits.title": "Kits",
  "kits.intro": "Ein Kit ist eine Kiste für einen Raum: ein kleiner Router mit eigenem WLAN, die Geräte, ein Koffer. Name und Passwort des WLANs werden hier erzeugt, wenn Sie das Kit anlegen — niemand muss sie sich ausdenken — und Ihnen für den Router und das iPad der Lehrkraft angezeigt. Sie werden nie per E-Mail verschickt.",
  "kits.new": "Neues Kit",
  "kits.newHelp": "Geben Sie ihm den Namen, der auf der Kiste stehen wird. Jedes Gerät, das dazu kommt, bekommt eine Beschriftung, die mit diesem Namen beginnt — „Set B, 07“.",
  "kits.create": "Kit anlegen",
  "kits.created": "{name} ist bereit. WLAN-Name und Passwort stehen unten.",
  "kits.none": "Noch keine Kits",
  "kits.noneBody":
    "Legen Sie für die Kiste jedes Raums eines an. WLAN-Name und Passwort werden für Sie erzeugt; die Geräte in der Kiste werden getrennt davon unter „Geräte“ registriert.",
  "kits.field.name": "Name",
  "kits.field.nameHint": "Kurz, so wie er auf der Kiste und auf jedem Gerät darin stehen wird.",
  "kits.field.router": "Router",
  "kits.field.routerHint": "Die App der Lehrkraft kann diesen Router mit einem Tipp einrichten.",
  "kits.router.gl-mt3000": "GL.iNet Beryl AX (GL-MT3000)",
  "kits.createdOn": "Angelegt am {date}",
  "kits.routerOnly": "Router: {model}. Diesen Router kann die App der Lehrkraft mit einem Tipp einrichten.",
  "kits.devices": "Geräte",
  "kits.devicesOwner": "Die Geräte dieses Kits werden unter „Geräte“ registriert, eines nach dem anderen über USB.",
  "kits.devicesTeacher": "Die Geräte dieses Kits registriert die Person, die Spontar in Ihrer Einrichtung betreut, unter „Geräte“. Hier ist dafür nichts zu tun.",
  "kits.network": "WLAN-Name",
  "kits.password": "WLAN-Passwort",
  "kits.showPassword": "Anzeigen",
  "kits.hidePassword": "Verbergen",
  "kits.copy": "Kopieren",
  "kits.copied": "Kopiert.",
  "kits.copyFailed": "Konnte nicht kopieren. Lesen Sie es stattdessen vom Bildschirm ab.",
  "kits.rename": "Umbenennen",
  "kits.renameHelp": "Das Umbenennen des Kits ändert die Beschriftungen nicht, die schon auf seinen Geräten stehen.",
  "kits.setNetwork": "WLAN festlegen",
  "kits.field.ssid": "WLAN-Name",
  "kits.field.psk": "WLAN-Passwort",
  "kits.network.help": "Lassen Sie beides so und stellen Sie den Router darauf ein. Wenn im Raum schon ein Router steht, den Sie lieber nicht ändern möchten, tragen Sie hier ein, was dieser bereits sagt.",
  "kits.network.consequence": "Jedes Gerät, das bereits zu diesem Kit gehört, kennt das alte Netz und muss angeschlossen und auf dieses umgestellt werden.",
  "kits.network.saved": "{name} ist im neuen Netz.",
  "kits.newPassword": "Neues Passwort",
  "kits.rotate.title": "Neues WLAN-Passwort für {name}",
  "kits.rotate.body": "Der Router und jedes Gerät in diesem Kit müssen vor der nächsten Stunde mit dem neuen Passwort neu eingerichtet werden. Der WLAN-Name bleibt gleich. Tippen Sie zur Bestätigung den Namen des Kits ein.",
  "kits.rotate.confirm": "Name des Kits",
  "kits.rotate.mismatch": "Das ist nicht der Name des Kits.",
  "kits.passwordRotated": "{name} hat ein neues WLAN-Passwort. Richten Sie den Router und seine Geräte neu ein.",
  "kits.checklist.title": "Router-Einstellungen",
  "kits.checklist.intro": "Die App der Lehrkraft setzt diese Einstellungen mit einem Tipp auf einem Router, den sie kennt. Auf einem anderen Router, oder von Hand, stellen Sie genau das ein:",
  "kits.checklist.ssid": "WLAN-Name:",
  "kits.checklist.psk": "WLAN-Passwort:",
  "kits.checklist.band24": "2,4 GHz an, fester Kanal {channels} — einen wählen.",
  "kits.checklist.band5": "5 GHz an, gleicher Name und gleiches Passwort.",
  "kits.checklist.isolation": "Client-Isolation aus — die Geräte müssen das iPad der Lehrkraft sehen können.",
  "kits.checklist.internetCheck": "Internetprüfung aus — der Router darf sich nicht beschweren, dass er offline ist.",
  "kits.checklist.noInternet": "Keine Internetverbindung. Das Kit arbeitet für sich.",
  "kits.firmware.title": "Gerätesoftware",
  "kits.firmware.version": "Geräte, die über diese Seite hinzugefügt werden, bekommen Version {version}.",
  "kits.firmware.unavailable": "Die Gerätesoftware konnte von dieser Seite nicht geladen werden.",
  "kits.firmware.placeholder": "Auf dieser Seite ist noch keine Gerätesoftware veröffentlicht. Bis dahin können keine Geräte hinzugefügt werden.",
  "kits.firmware.nothingToInstall": "Neue Geräte bekommen diese Version, sobald sie hinzugefügt werden. Sie müssen nichts installieren.",
  "kits.firmware.manifestLink": "Software-Manifest, für Ihre IT-Abteilung",
  "kit.error.notFound": "Dieses Kit wurde nicht gefunden.",
  "kit.error.nameEmpty": "Ein Kit braucht einen Namen.",
  "kit.error.nameTooLong": "Dieser Name ist zu lang.",
  "kit.error.ssidEmpty": "Ein Netz braucht einen Namen.",
  "kit.error.ssidTooLong": "Ein WLAN-Name darf höchstens 32 Zeichen haben.",
  "kit.error.pskTooShort": "Ein WLAN-Passwort muss mindestens 8 Zeichen haben.",
  "kit.error.pskTooLong": "Ein WLAN-Passwort darf höchstens 63 Zeichen haben.",
  "enrolment.error.labelRequired": "Beim Eintragen von Hand wird eine Beschriftung gebraucht.",
  "nav.library": "Bibliothek",
  "nav.sessions": "Stunden",
  "nav.reports": "Auswertungen",
  "nav.institution": "Einrichtung",
  "nav.billing": "Abrechnung",
  "nav.legal": "Rechtliches",
  "nav.consent": "Einwilligungen",
  "nav.signOut": "Abmelden",
  "nav.menu": "Menü",
  "nav.skipToContent": "Zum Inhalt springen",
  "nav.openComposer": "Composer öffnen",
  "nav.openComposerHint": "Szenen schreiben Sie in Composer. Dieselbe Anmeldung.",

  // ── Anmeldung ─────────────────────────────────────────────────────
  "auth.signIn": "Anmelden",
  "auth.email": "E-Mail",
  "auth.username": "Benutzername",
  "auth.password": "Passwort",
  "auth.signingIn": "Anmeldung läuft …",
  "auth.identifier": "E-Mail oder Benutzername",
  "auth.identifierHint":
    "Was auf Ihrem Zettel steht. Ein Benutzername enthält kein @.",
  "auth.action.title": "Passwort wählen",
  "auth.action.checking": "Ihr Link wird geprüft …",
  "auth.action.for": "Für {email}. Dasselbe Passwort öffnet Backstage und Composer.",
  "auth.action.save": "Passwort setzen und anmelden",
  "auth.action.invalid":
    "Dieser Link ist abgelaufen oder wurde schon benutzt. Ein Link gilt einmal, eine Stunde lang. Einen neuen bekommen Sie auf der Anmeldeseite unter „Passwort vergessen?“ oder von der Person, die Ihr Konto eingerichtet hat.",
  "auth.action.done": "Ihr Passwort ist gesetzt. Melden Sie sich damit an.",
  "auth.action.toSignIn": "Zur Anmeldung",
  "home.teacher.groups": "Ihre Kurse",
  "home.teacher.noGroups": "Sie sind noch keinem Kurs zugeordnet. Die Person, die Spontar in Ihrer Einrichtung betreut, trägt Sie ein.",
  "home.teacher.allGroups": "Alle Kurse",
  "home.teacher.students": "{count} Teilnehmende",
  "home.teacher.next": "Wo gearbeitet wird",
  "home.teacher.composerBody": "Hier schreiben Sie die Szenen, die Sie unterrichten. Dieselbe Anmeldung gilt dort.",
  "home.teacher.lessons": "Stunden",
  "home.teacher.lessonsBody": "Was in einer Stunde passiert ist, zum Nachlesen. Die Stunde selbst läuft in der Lehrkraft-App auf Ihrem Tablet.",
  "home.teacher.kits": "Kits",
  "home.teacher.kitsBody": "WLAN-Name und Passwort der Kiste in Ihrem Raum, für Router und Tablet.",
  "auth.sameAccount": "Dieselbe Anmeldung wie in Composer.",
  "auth.forgotPassword": "Passwort vergessen?",
  "auth.forgotPasswordHelp":
    "Tragen Sie oben Ihre E-Mail-Adresse ein, dann schicken wir Ihnen einen Link.",
  "auth.sendResetLink": "Link zum Zurücksetzen senden",
  "auth.resetLinkSent":
    "Falls zu dieser Adresse ein Konto gehört, ist ein Link unterwegs. Er gilt eine Stunde.",
  "auth.resetNeedsEmail":
    "Dieses Konto meldet sich mit einem Benutzernamen an und hat keine E-Mail-Adresse — es gibt also kein Ziel für einen Link. Die Person, die Spontar in Ihrer Einrichtung verwaltet, vergibt in Sekunden neue Zugangsdaten.",
  "auth.changePassword": "Eigenes Passwort wählen",
  "auth.changePasswordWhy":
    "Ihr bisheriges Passwort stand auf Papier. Ersetzen Sie es, bevor Sie weiterarbeiten.",
  "auth.newPassword": "Neues Passwort",
  "auth.confirmPassword": "Wiederholen",
  "password.tooShort": "Mindestens acht Zeichen.",
  "password.needsUppercase": "Es fehlt ein Großbuchstabe.",
  "password.needsLowercase": "Es fehlt ein Kleinbuchstabe.",
  "password.needsNumber": "Es fehlt eine Ziffer.",
  "password.rules": "Mindestens acht Zeichen, mit einem Großbuchstaben, einem Kleinbuchstaben und einer Ziffer.",
  "auth.passwordMismatch": "Die beiden stimmen nicht überein.",
  "auth.save": "Speichern",

  // ── Rollen ────────────────────────────────────────────────────────
  "role.owner": "Leitung",
  "role.teacher": "Lehrkraft",
  "role.student": "Teilnehmer:in",
  "role.spontar_admin": "Spontar",

  // ── Allgemein ─────────────────────────────────────────────────────
  "common.save": "Speichern",
  "common.cancel": "Abbrechen",
  "common.close": "Schließen",
  "common.delete": "Löschen",
  "common.confirm": "Bestätigen",
  "common.edit": "Bearbeiten",
  "common.add": "Hinzufügen",
  "common.search": "Suchen",
  "common.loading": "Wird geladen …",
  "common.saving": "Wird gespeichert …",
  "common.saved": "Gespeichert",
  "common.retry": "Erneut versuchen",
  "common.language": "Sprache",
  "common.appLanguage": "Sprache der App",
  "common.none": "Keine",
  "common.yes": "Ja",
  "common.no": "Nein",
  "common.back": "Zurück",
  "common.required": "Erforderlich",
  "common.optional": "Optional",
  "common.opensInNewTab": "öffnet sich in einem neuen Tab",
  "toast.repeat": "×{count}",

  // ── Übersicht ─────────────────────────────────────────────────────
  "dashboard.title": "Übersicht",
  "dashboard.welcome": "Guten Tag, {name}.",
  "dashboard.empty": "Hier ist noch nichts passiert. Legen Sie zuerst eine Gruppe an.",

  // ── Onboarding ────────────────────────────────────────────────────
  "onboarding.title": "Erste Schritte",
  "onboarding.intro":
    "Fünf Dinge, der Reihe nach. Jedes führt dorthin, wo es erledigt wird; die Liste hält sich selbst aktuell und verschwindet, wenn alles erledigt ist.",
  "onboarding.progress": "{done} von {total} erledigt",
  "onboarding.dismiss": "Vorerst ausblenden",
  "onboarding.showAgain": "Einrichtungsschritte wieder anzeigen",
  "onboarding.step.legal": "Rechtliche Dokumente annehmen",
  "onboarding.step.legal.hint":
    "{count} Dokument wartet noch auf Ihre Annahme. Bis dahin lassen sich keine Stunden starten.",
  "onboarding.step.legal.hintPlural":
    "{count} Dokumente warten noch auf Ihre Annahme. Bis dahin lassen sich keine Stunden starten.",
  "onboarding.step.teachers": "Ihre Lehrkräfte hinzufügen",
  "onboarding.step.teachers.hint":
    "Jede Lehrkraft, die Sie mit E-Mail-Adresse hinzufügen, bekommt einen Link und wählt ihr Passwort selbst; ohne Adresse kommt sie über eine Klassenliste mit einem gedruckten Passwort herein. Sie selbst sind bereits Lehrkraft und müssen nicht hinzugefügt werden.",
  "onboarding.step.group": "Teilnehmende hinzufügen und in eine Gruppe aufnehmen",
  "onboarding.step.group.hintNoPlayers":
    "Fügen Sie Teilnehmende einzeln hinzu oder importieren Sie eine Klassenliste. Teilnehmende haben kein Passwort — sie halten in der Stunde ein Gerät.",
  "onboarding.step.group.hintNoGroup":
    "Eine Gruppe ist eine Klasse oder ein Kurs. Jede Stunde wird mit einer gehalten.",
  "onboarding.step.group.hintEmptyGroup": "{name} hat noch niemanden auf der Liste. Nehmen Sie Teilnehmende auf.",
  "onboarding.step.devices": "Ein Kit und seine Geräte einrichten",
  "onboarding.step.devices.hint":
    "Ein Kit ist die Kiste für einen Raum: ein eigenes WLAN und die Geräte der Teilnehmenden. Legen Sie zuerst das Kit an; die Geräte kommen dann dazu.",
  "onboarding.step.composer": "Composer öffnen und die erste Szene schreiben",
  "onboarding.step.composer.hint":
    "Composer ist die App, in der Lehrkräfte Szenen schreiben. Ihre Anmeldung gilt auch dort.",
  "onboarding.teacher.title": "Was Sie hier tun können",
  "onboarding.teacher.intro":
    "In Backstage wird Ihre Einrichtung geführt — Personen, Gruppen, Geräte und das, was in vergangenen Stunden geschehen ist. Drei Orte sind für eine Lehrkraft wichtig:",
  "onboarding.teacher.groups": "Gruppen",
  "onboarding.teacher.groupsBody": "Die Klassen und Kurse, die Sie unterrichten, mit allen auf der Liste.",
  "onboarding.teacher.lessons": "Stunden",
  "onboarding.teacher.lessonsBody":
    "Was in einer Stunde geschehen ist, im Nachhinein. Die Stunde selbst führen Sie mit der Lehrkraft-App auf Ihrem Tablet.",
  "onboarding.teacher.kits": "Kits",
  "onboarding.teacher.kitsBody":
    "WLAN-Name und Passwort der Kiste in Ihrem Raum, für den Router und Ihr Tablet.",
  "onboarding.teacher.composerBody":
    "Die Szenen, die Sie unterrichten, werden in Composer geschrieben, einer eigenen App. Ihr Benutzername und Ihr Passwort gelten auch dort.",
  "onboarding.teacher.dismiss": "Verstanden",

  // ── Fehler ────────────────────────────────────────────────────────
  "error.unexpected": "Etwas ist schiefgelaufen. Der Fehler wurde protokolliert.",
  "error.network.unavailable":
    "Der Server war nicht erreichbar. Prüfen Sie die Verbindung und versuchen Sie es erneut.",
  "error.server.broken":
    "Bei uns ist etwas kaputt. Es liegt nicht an Ihrer Verbindung — wir wissen davon. Versuchen Sie es in ein paar Minuten noch einmal.",
  "error.auth.required": "Bitte melden Sie sich an.",
  "error.auth.mustChangePassword": "Wählen Sie zuerst ein eigenes Passwort.",
  "error.auth.invalidCredentials": "Benutzername und Passwort passen nicht zusammen.",
  "error.permission.denied": "Dafür fehlt Ihnen die Berechtigung.",
  "error.permission.disabled": "Dieses Konto ist deaktiviert.",
  "error.permission.noInstitution": "Dieses Konto gehört zu keiner Einrichtung.",
  "error.user.notFound": "Diese Person wurde nicht gefunden.",
  "error.user.cannotDisableSelf": "Sie können Ihr eigenes Konto nicht deaktivieren.",
  "error.user.cannotDeleteSelf": "Sie können Ihr eigenes Konto nicht löschen.",
  "error.user.cannotChangeOwnRole": "Sie können Ihre eigene Rolle nicht ändern.",
  "error.user.disabled": "Dieses Konto ist deaktiviert.",
  "error.institution.notFound": "Einrichtung nicht gefunden.",
  "error.group.notFound": "Diese Gruppe wurde nicht gefunden.",
  "error.group.needsATeacher": "Eine Gruppe braucht mindestens eine Lehrkraft.",
  "error.group.lastTeacher":
    "Das ist die einzige Lehrkraft dieser Gruppe. Fügen Sie zuerst eine weitere hinzu.",
  "error.group.notATeacher": "Diese Person ist keine Lehrkraft.",
  "error.group.notAStudent": "Diese Person ist keine Teilnehmerin und kein Teilnehmer.",
  "error.group.mustTeachOwnGroup": "Sie können nur eine Gruppe anlegen, die Sie selbst unterrichten.",
  "error.validation.invalidInput": "In diesem Formular stimmt etwas nicht.",
  "error.validation.displayNameEmpty": "Ein Name fehlt.",
  "error.validation.displayNameTooLong": "Dieser Name ist zu lang.",
  "error.validation.email": "Das sieht nicht nach einer E-Mail-Adresse aus.",
  "error.validation.groupNameEmpty": "Eine Gruppe braucht einen Namen.",
  "error.validation.groupNameTooLong": "Dieser Name ist zu lang.",
  "error.validation.seatsExceedTier": "Das sind mehr Lehrkräfte, als der Tarif {tier} abdeckt.",
  "error.validation.validUntilInPast": "Dieses Datum liegt bereits in der Vergangenheit.",
  "error.validation.date": "Das ist kein Datum, das wir lesen können.",
  "error.validation.rangeReversed": "Das Ende des Zeitraums liegt vor seinem Anfang.",
  "error.validation.reasonRequired": "Bitte geben Sie einen Grund an.",
  "error.validation.retentionOutOfRange": "Zwischen {min} und {max} Tagen.",
  "error.validation.retentionTooShort": "Kürzer können Aufzeichnungen nicht aufbewahrt werden.",
  "error.validation.retentionTooLong": "Länger bewahren wir Aufzeichnungen nicht auf.",
  "error.forbidden.title": "Nicht für Sie",
  "error.forbidden.bodyNamed":
    "{page} ist der Leitung der Einrichtung vorbehalten. Wenn Sie Zugang brauchen, wenden Sie sich an die Person, die Spontar bei Ihnen betreut.",
  "error.forbidden.back": "Zurück zur Übersicht",
  "error.forbidden.body":
    "Diese Seite ist der Leitung vorbehalten. Wenn Sie Zugriff brauchen, wenden Sie sich an sie.",
  "error.notFound.title": "Hier ist nichts",
  "error.notFound.body": "Der Link ist vielleicht alt, oder die Seite ist umgezogen.",

  // ── Abrechnung ────────────────────────────────────────────────────
  "billing.error.seatLimit":
    "Ihre Lizenz {tier} umfasst {max} Lehrkräfte, {used} sind belegt. Für eine weitere ist ein größerer Tarif nötig.",
  "billing.error.studentLimit":
    "Ihre Lizenz umfasst {max} Teilnehmende, und {used} sind in Gebrauch. Schreiben Sie uns für mehr.",
  "billing.error.licenceExpired": "Die Lizenz ist am {validUntil} abgelaufen.",
  "billing.error.suspended": "Diese Einrichtung wurde gesperrt.",
  "billing.error.noSubscription": "Es gibt noch kein Abonnement zu verwalten.",
  "billing.error.tooManyTeachersForTier":
    "Dieser Tarif umfasst {max} Lehrkräfte, {used} sind belegt. Entfernen Sie zuerst einige.",
  "billing.error.vatIdMalformed": "Diese USt-IdNr. hat nicht das richtige Format.",
  "billing.error.vatIdCountryMismatch": "Diese USt-IdNr. gehört zu einem anderen Land.",
  "billing.error.vatIdNotFound": "VIES kennt diese USt-IdNr. nicht.",
  "billing.error.vatValidationUnavailable":
    "VIES antwortet nicht. Bis zur Prüfung wird Umsatzsteuer berechnet; versuchen Sie es später erneut.",

  // ── Rechtliches und Stunden ───────────────────────────────────────
  "legal.error.documentsOutstanding": "Einige Dokumente müssen noch bestätigt werden.",
  "legal.error.consentMissing": "Für einige Teilnehmende fehlt die Einwilligung der Eltern.",
  "legal.error.versionSuperseded":
    "Es gibt eine neuere Fassung. Laden Sie die Seite neu und lesen Sie sie.",
  "legal.error.documentNotFound": "Dieses Dokument wurde nicht gefunden.",
  "session.error.notFound": "Diese Stunde wurde nicht gefunden.",
  "session.error.notHost": "Nur die Lehrkraft, die eine Stunde leitet, kann sie steuern.",
  "session.error.reviewIsTeacherOnly":
    "Die Details einer Stunde öffnet nur die Lehrkraft, die sie gehalten hat.",
  "session.error.groupArchived": "Diese Gruppe ist archiviert.",
  "session.error.alreadyEnded": "Diese Stunde ist bereits beendet.",
  "session.error.codeNotFound": "Zu diesem Code läuft keine Stunde.",
  "session.error.notOnRoster": "Sie stehen nicht auf der Liste dieser Gruppe.",

  // ── Inhalte und Import ────────────────────────────────────────────
  "scenario.error.notFound": "Diese Szene wurde nicht gefunden.",
  "scenario.error.staleEdit":
    "{editedBy} hat gespeichert, während Sie bearbeitet haben. Bitte neu laden.",
  "scenario.error.invalid": "Diese Szene lässt sich noch nicht veröffentlichen.",
  "scenario.error.versionSuperseded": "Es gibt bereits eine neuere Fassung.",
  "import.error.fileTooLarge": "Diese Datei ist zu groß. Eine Klassenliste ist nie so groß.",
  "import.error.emptyFile": "Diese Datei ist leer.",
  "import.error.unsupportedFileType": "Spontar liest CSV- und XLSX-Dateien.",
  "import.error.noReadableRows": "In dieser Datei war nichts Lesbares zu finden.",
  "import.error.tooManyRows": "Das sind mehr als {max} Zeilen.",
  "import.error.nothingToImport": "Es gibt nichts zu importieren.",
  "import.error.ageBandRequiredForNewGroup":
    "Die Gruppe {group} existiert noch nicht, und eine neue Gruppe braucht eine Altersstufe.",
  "import.error.noOwnerForNewGroup": "Eine neue Gruppe braucht eine zuständige Lehrkraft.",
  "ai.error.budgetExceeded": "Das Budget dieses Monats für den Schreibassistenten ist aufgebraucht.",
  "ai.error.providerUnavailable": "Der Schreibassistent antwortet nicht. Versuchen Sie es gleich erneut.",
  "ai.error.unparseableOutput": "Der Assistent hat etwas Unbrauchbares geliefert. Bitte erneut versuchen.",
  "ai.error.generatedScenarioInvalid":
    "Der Assistent hat eine Szene erzeugt, die mitten im Spiel hängen bliebe. Formulieren Sie den Auftrag anders.",
  "ai.error.studentDataBlocked":
    "Diese Anfrage wurde blockiert, weil sie Daten von Teilnehmenden enthielt.",
  "enrolment.error.notFound": "Dieses Gerät ist nicht registriert.",
  "contact.error.email": "Das sieht nicht nach einer E-Mail-Adresse aus.",
  "contact.error.missingFields": "Bitte füllen Sie die erforderlichen Felder aus.",

  // ── Einrichtung ───────────────────────────────────────────────────
  "institution.title": "Einrichtung",
  "institution.section.identity": "Angaben",
  "institution.section.defaults": "Voreinstellungen für neue Gruppen",
  "institution.section.retention": "Wie lange Aufzeichnungen bleiben",
  "institution.section.billing": "Rechnungsangaben",
  "institution.name": "Name",
  "institution.country": "Land",
  "institution.countryFixed":
    "Das Land bestimmt, welche Rechtsdokumente gelten — deshalb lässt es sich nicht selbst ändern. Schreiben Sie uns, wir stellen es um.",
  "institution.segment": "Art der Einrichtung",
  "institution.segment.studio": "Studio",
  "institution.segment.school": "Schule",
  "institution.locale": "Sprache",
  "institution.locale.en": "Englisch",
  "institution.locale.de": "Deutsch",
  "institution.locale.fr": "Französisch",
  "institution.localeHint":
    "Die Sprache der Zugangszettel und unserer E-Mails. Jede Person kann ihre eigene wählen.",
  "institution.defaultSessionMode": "Wie eine Stunde verbindet",
  "institution.mode.online": "Online",
  "institution.mode.offline": "Offline",
  "institution.modeHint":
    "Offline läuft die ganze Stunde im lokalen Netz. Die Lehrkraft kann beim Start beides wählen.",
  "institution.assessmentPreset": "Bewertungsrahmen",
  "institution.preset.ncas": "NCAS (USA)",
  "institution.preset.aoao": "AO1–AO4 (England und Wales)",
  "institution.preset.epa": "EPA / KMK (Deutschland)",
  "institution.preset.custom": "Eigener",
  "institution.retentionDays": "Aufzeichnungen einer Stunde bleiben",
  "institution.retentionUnit": "Tage",
  "institution.retentionHint":
    "Wer welche Rolle gespielt hat, die Bewertungen der Lehrkraft, ihre Notizen. Statistiken bleiben dauerhaft und enthalten keine Namen.",
  "institution.retentionShortWarning":
    "Unter 90 Tagen verliert eine Lehrkraft die Aufzeichnungen des letzten Halbjahres, bevor dieses endet. Das dürfen Sie so wählen — gemeint ist es selten.",
  "institution.billingEmail": "Wohin Rechnungen gehen",
  "institution.vatId": "Umsatzsteuer-Identifikationsnummer",
  "institution.vatIdHint":
    "Wird über VIES geprüft. Eine ungeprüfte Nummer gilt als nicht vorhanden.",
  "institution.vat.validated": "Geprüft am {date}. Es gilt das Reverse-Charge-Verfahren.",
  "institution.vat.none": "Keine USt-IdNr. Es wird der Steuersatz Ihres Landes berechnet.",
  "institution.vat.outsideScope": "Außerhalb des Anwendungsbereichs der EU-Umsatzsteuer.",
  "institution.vat.charge": "Umsatzsteuer wird berechnet.",
  "institution.vat.revalidate": "Erneut prüfen",
  "institution.vat.remove": "Entfernen",
  "institution.saved": "Gespeichert.",

  // ── Rechtliches ───────────────────────────────────────────────────
  "legal.title": "Rechtliches",
  "legal.docType.tos": "Allgemeine Geschäftsbedingungen",
  "legal.docType.dpa": "Auftragsverarbeitungsvertrag (englisch)",
  "legal.docType.avv": "Auftragsverarbeitungsvertrag",
  "legal.docType.ferpa_addendum": "FERPA-Zusatz",
  "legal.docType.state_addendum": "Bundesstaat-Zusatz",
  "legal.docType.privacy": "Datenschutzerklärung",
  "legal.docType.privacy_child": "Datenschutzerklärung in einfacher Sprache",
  "legal.docType.impressum": "Impressum",
  "legal.docType.parental_consent_template": "Vorlage für die Einwilligung der Eltern",
  "legal.docType.law25_addendum": "Zusatz nach Law 25 (Québec)",
  "legal.intro":
    "Was für Sie gilt, entscheiden Ihr Land und die Art Ihrer Einrichtung — weder wir noch Sie.",
  "legal.column.document": "Dokument",
  "legal.column.version": "Fassung",
  "legal.column.status": "Status",
  "legal.column.published": "Veröffentlicht",
  "legal.column.actions": "Was Sie tun können",
  "legal.status.accepted": "Von Ihrer Einrichtung angenommen",
  "legal.status.newVersion": "Neue Fassung — Handlung nötig",
  "legal.status.never": "Noch nicht angenommen",
  "legal.status.optional": "Zur Kenntnis",
  "legal.acceptedVersion": "Sie haben Fassung {version} angenommen",
  "legal.read": "Lesen",
  "legal.openOnSite": "Veröffentlichte Seite öffnen",
  "legal.accept": "Annehmen",
  "legal.acceptedToast": "Erfasst.",
  "legal.scrollToEnd": "Bitte bis zum Ende lesen, dann annehmen.",
  "legal.acceptOnBehalf":
    "Die Annahme bindet {institution}. Ihr Name, die Fassung und der Zeitpunkt werden festgehalten.",
  "legal.error.bodyUnavailable":
    "Der Text ließ sich nicht laden. Öffnen Sie so lange die veröffentlichte Seite und versuchen Sie es gleich erneut.",
  "legal.gapsTitle": "Was bei uns fehlt",
  "legal.gapsBody":
    "Diese Dokumente gelten für Sie, und wir haben sie noch nicht veröffentlicht. Das liegt an uns, nicht an Ihnen. Wir melden uns, sobald sie fertig sind.",
  "legal.outstandingBanner":
    "{count} Dokument wartet auf Ihre Annahme. Bis dahin lassen sich keine Stunden starten.",
  "legal.outstandingBannerPlural":
    "{count} Dokumente warten auf Ihre Annahme. Bis dahin lassen sich keine Stunden starten.",
  "legal.outstandingAction": "Ansehen",
  "legal.blockingTitle": "Neue Fassung zu lesen",
  "legal.blockingBody":
    "Diese Dokumente haben sich seit Ihrer Annahme geändert. Bitte lesen und annehmen Sie sie, bevor Sie weiterarbeiten.",
  "legal.blockingSignOut": "Stattdessen abmelden",
  "legal.gate.firstRunTitle": "Bevor es losgeht",
  "legal.gate.firstRunBody":
    "{institution} ist eingerichtet. Lesen und akzeptieren Sie, was Ihre Einrichtung mit Spontar vereinbart — das dauert ein paar Minuten und geschieht einmal. Backstage öffnet sich, sobald alles unten angenommen ist.",
  "legal.gate.readAndAccept": "Lesen und annehmen",
  "legal.gate.ownerOnly":
    "Annehmen kann nur die Leitung der Einrichtung. Lehrkräfte können sich anmelden, sobald das geschehen ist.",
  "legal.gate.teacherTitle": "Die Leitung Ihrer Einrichtung muss zuerst die Bedingungen annehmen",
  "legal.gate.teacherBody":
    "Bevor jemand bei {institution} Backstage nutzen kann, muss die Leitung Folgendes lesen und annehmen:",
  "legal.gate.teacherNext":
    "Mit Ihrem Konto ist alles in Ordnung. Bitten Sie die Leitung, sich einmal anzumelden und anzunehmen; danach sind Sie drin.",
  "legal.gate.unavailableTitle": "Die rechtlichen Unterlagen Ihrer Einrichtung ließen sich nicht prüfen",
  "legal.gate.unavailableBody":
    "Backstage öffnet sich, sobald das geprüft ist. Versuchen Sie es gleich noch einmal.",

  // ── Unterauftragsverarbeiter und DSFA ─────────────────────────────
  "legal.subprocessors.title": "Wer sonst Ihre Daten verarbeitet",
  "legal.subprocessors.body":
    "Jedes Unternehmen, das Daten Ihrer Einrichtung berührt, wofür und wo. Maßgeblich ist die veröffentlichte Liste.",
  "legal.subprocessors.view": "Veröffentlichte Liste ansehen",
  "legal.dpia.title": "Datenschutz-Folgenabschätzung",
  "legal.dpia.body":
    "Eine DSFA stellen wir auf Anfrage bereit. Für eine Theaterstunde brauchen die meisten Einrichtungen keine; eine öffentliche Schule mit Datenschutzbeauftragtem fragt in der Regel danach.",
  "legal.dpia.request": "Bei uns anfragen",

  // ── Einwilligung der Eltern ───────────────────────────────────────
  "consent.title": "Einwilligung der Eltern",
  "consent.intro":
    "Gruppen mit Minderjährigen brauchen eine hinterlegte Einwilligung, bevor eine Stunde starten kann. Sie haben den Kontakt zur Familie; wir treten nie an sie heran.",
  "consent.noGroups": "Noch braucht keine Gruppe eine Einwilligung.",
  "consent.group.recorded": "{recorded} erfasst",
  "consent.group.missing": "{count} fehlen noch",
  "consent.group.complete": "Vollständig",
  "consent.notRequired": "Für diese Gruppe nicht erforderlich",
  "consent.record": "Einwilligung erfassen",
  "consent.revoke": "Widerrufen",
  "consent.method": "Wie sie erteilt wurde",
  "consent.method.paper_form": "Unterschriebenes Formular",
  "consent.method.digital_signature": "Digitale Unterschrift",
  "consent.method.verbal_recorded": "Persönlich erteilt und notiert",
  "consent.method.school_policy": "Durch die Schulordnung abgedeckt",
  "consent.note": "Notiz",
  "consent.noteHint":
    "Zum Beispiel, wo das Formular abgelegt ist. Nie Name oder Anschrift eines Elternteils.",
  "consent.recorded": "Für {name} erfasst.",
  "consent.revoked": "Für {name} widerrufen.",
  "consent.missingHeading": "Fehlen noch",
  "consent.recordedHeading": "Hinterlegt",

  // ── Personen ──────────────────────────────────────────────────────
  "people.title": "Personen",
  "people.add": "Person hinzufügen",
  "people.import": "Liste importieren",
  "people.search": "Nach Name oder Benutzername suchen",
  "people.filter.role": "Rolle",
  "people.filter.group": "Gruppe",
  "people.filter.status": "Status",
  "people.filter.all": "Alle",
  "people.filter.hasDevices": "Nur Personen mit registriertem Gerät",
  "people.column.name": "Name",
  "people.column.role": "Rolle",
  "people.column.username": "Benutzername",
  "people.column.groups": "Gruppen",
  "people.column.devices": "Geräte",
  "people.column.lastActive": "Zuletzt gesehen",
  "people.column.status": "Status",
  "people.status.active": "Aktiv",
  "people.status.disabled": "Deaktiviert",
  "people.status.mustChangePassword": "Hat noch kein eigenes Passwort",
  "people.never": "Nie",
  "people.empty":
    "Niemand passt dazu",
  "people.emptyBody":
    "Ändern Sie die Filter, fügen Sie eine Lehrkraft von Hand hinzu oder importieren Sie eine Klassenliste.",
  "people.selected": "{count} ausgewählt",
  "people.bulk.assignGroup": "Zu einer Gruppe hinzufügen",
  "people.bulk.resetPassword": "Neue Passwörter vergeben",
  "people.bulk.revokeDevices": "Geräte sperren",
  "people.bulk.disable": "Deaktivieren",
  "people.bulk.delete": "Löschen",
  "people.confirm.typeCount": "Zur Bestätigung {count} eingeben",
  "people.confirm.countMismatch": "Das ist nicht die Zahl.",
  "people.confirm.deleteTitle": "{count} Personen löschen",
  "people.confirm.deleteBody":
    "Ihre Konten, Gruppenzugehörigkeiten und Stundenaufzeichnungen werden entfernt. Statistiken bleiben unberührt — sie enthalten keine Namen. Das lässt sich nicht rückgängig machen.",
  "people.confirm.resetTitle": "{count} Personen neue Passwörter geben",
  "people.confirm.resetBody":
    "Die bisherigen Passwörter gelten sofort nicht mehr. Die neuen stehen auf einem frischen Blatt, das Sie austeilen müssen.",
  "people.confirm.revokeTitle": "Geräte von {count} Personen sperren",
  "people.confirm.revokeBody":
    "Jedes Gerät muss vor der nächsten Stunde neu registriert werden. Sinnvoll, wenn ein Handy verloren geht oder jemand die Einrichtung verlässt.",
  "people.confirm.disableTitle": "{count} Personen deaktivieren",
  "people.confirm.disableBody":
    "Sie können sich nicht mehr anmelden. Es wird nichts gelöscht, und Sie können es auf der Seite jeder Person rückgängig machen.",
  "people.reason": "Grund",
  "people.reasonHint": "Wird im Protokoll festgehalten. Ein Satz genügt.",
  "people.intro":
    "Alle an Ihrer Einrichtung: Lehrkräfte, die sich anmelden, und Teilnehmende, die in der Stunde ein Gerät halten und sich nie anmelden. Sie stehen als Leitung in der Liste und zählen als Lehrkraft — Sie müssen sich nicht selbst hinzufügen.",
  "people.role.ownerYou": "Leitung (Sie)",
  "people.mark.youOwner": "(Sie, Leitung)",
  "people.mark.owner": "(Leitung)",

  // ── eine Person ───────────────────────────────────────────────────
  "person.title": "{name}",
  "person.section.identity": "Angaben",
  "person.section.groups": "Gruppen",
  "person.section.devices": "Geräte",
  "person.section.data": "Ihre Daten",
  "person.displayName": "Name",
  "person.username": "Benutzername",
  "people.noUsername": "Teilnehmende halten ein registriertes Gerät und melden sich nie an, also gibt es keinen Benutzernamen.",
  "people.noUsernameMark": "—",
  "person.noUsername": "Keiner — diese Person hält ein registriertes Gerät und meldet sich nie an.",
  "person.email": "E-Mail",
  "person.emailNone":
    "Keine. Teilnehmende erhalten keine E-Mails, und es wird auch keine Adresse erhoben.",
  "person.role": "Rolle",
  "person.created": "Hinzugefügt",
  "person.lastActive": "Zuletzt gesehen",
  "person.noGroups": "Noch in keiner Gruppe",
  "person.noDevices": "Kein Gerät registriert",
  "person.device.enrolled": "Registriert am {date}",
  "person.device.lastSeen": "Zuletzt gesehen {date}",
  "person.device.revoked": "Gesperrt am {date}",
  "person.device.revoke": "Sperren",
  "person.resetPassword": "Neues Passwort vergeben",
  "person.disable": "Deaktivieren",
  "person.enable": "Wieder aktivieren",
  "person.export": "Alles herunterladen, was wir gespeichert haben",
  "person.delete": "Diese Person löschen",
  "person.deleteTitle": "{name} löschen",
  "person.deleteBody":
    "Konto, Gruppenzugehörigkeiten, registrierte Geräte und Stundenaufzeichnungen werden entfernt. Statistiken bleiben unberührt — sie enthalten keine Namen. Das lässt sich nicht rückgängig machen.",
  "person.deleteConfirm": "Zur Bestätigung {name} eingeben",
  "person.deleteMismatch": "Das ist nicht der Name.",
  "person.noHistory":
    "Stundenaufzeichnungen erreichen Sie über die Stunde, nicht über die Person. Sie gehören der Lehrkraft, die sie gehalten hat.",
  "person.passwordIssued": "Neues Passwort für {name}: {password}",
  "person.passwordIssuedWarning":
    "Schreiben Sie es jetzt auf. Es wird einmal angezeigt und lässt sich nicht wieder abrufen.",
  "person.noCredentials": "{name} steht im Verzeichnis. In der Stunde halten sie ein eingetragenes Gerät und melden sich nie an.",

  // ── Person hinzufügen ─────────────────────────────────────────────
  "newPerson.title": "Person hinzufügen",
  "newPerson.role": "Wer ist das?",
  "newPerson.role.teacher": "Eine Lehrkraft",
  "newPerson.role.student": "Teilnehmende:r",
  "newPerson.name": "Name",
  "newPerson.email": "E-Mail-Adresse",
  "newPerson.emailHint":
    "Dorthin gehen der Anmeldelink und spätere Passwort-Zurücksetzungen. Eine Lehrkraft ohne Adresse kommt nur über einen Klassenlisten-Import herein.",
  "newPerson.noEmailForStudents":
    "Teilnehmende halten in der Stunde ein Gerät und melden sich nie an, also gibt es nichts zu verschicken: kein Passwort, und nirgends in Spontar wird für sie eine E-Mail-Adresse erhoben — die größte einzelne Datenschutzentscheidung im Produkt.",
  "newPerson.locale": "Sprache",
  "newPerson.create": "Hinzufügen",
  "newPerson.created": "{name} hinzugefügt.",
  "newPerson.invited": "Ein Anmeldelink ist unterwegs an {email}. Er nennt beide Apps.",
  "newPerson.inviteNotSent":
    "{name} wurde hinzugefügt, aber die E-Mail ließ sich nicht senden. Schreiben Sie uns, und wir schicken den Link erneut.",

  // ── Import ────────────────────────────────────────────────────────
  "import.title": "Liste importieren",
  "import.step.upload": "Die Datei",
  "import.step.mapping": "Die Spalten",
  "import.step.review": "Die Personen",
  "import.step.credentials": "Die Passwörter",
  "import.upload.drop": "CSV oder Tabelle hierher ziehen oder Datei auswählen",
  "import.upload.choose": "Datei auswählen",
  "import.upload.limit": "CSV oder XLSX, bis 2 MB. Eine Klassenliste ist nie größer.",
  "import.upload.sheet": "Welches Tabellenblatt?",
  "import.upload.reading": "Datei wird gelesen …",
  "import.upload.rowsFound": "{count} Zeilen, {columns} Spalten",
  "import.upload.skippedLeading":
    "{count} Zeilen über der Kopfzeile wurden übergangen — eine Überschrift oder Leerzeilen.",
  "import.mapping.title": "Was in welcher Spalte steht",
  "import.mapping.fromAi":
    "Vorschlag des Schreibassistenten. Es wird nichts angelegt, bevor Sie jede Zeile gesehen haben.",
  "import.mapping.fromHeuristic":
    "Über die Spaltennamen zugeordnet. Der Schreibassistent war hier nicht beteiligt — prüfen Sie sorgfältiger als sonst.",
  "import.mapping.confidence.high": "Sicher",
  "import.mapping.confidence.medium": "Ziemlich sicher",
  "import.mapping.confidence.low": "Unsicher",
  "import.mapping.field.displayName": "Name",
  "import.mapping.field.displayNameSplit": "Name, über zwei Spalten",
  "import.mapping.field.role": "Rolle",
  "import.mapping.field.group": "Gruppe",
  "import.mapping.field.ageBand": "Alter oder Jahrgang",
  "import.mapping.none": "Nicht in dieser Datei",
  "import.mapping.first": "Erster Teil",
  "import.mapping.second": "Zweiter Teil",
  "import.mapping.splitToggle": "Der Name steht in zwei Spalten",
  "import.mapping.preview": "Vorschau",
  "import.mapping.continue": "Personen ansehen",
  "import.note.headerRowGuessed": "Die Kopfzeile stand unterhalb anderer Zeilen.",
  "import.note.nameSplitAcrossColumns": "Der Name scheint auf zwei Spalten verteilt zu sein.",
  "import.note.roleColumnAbsent":
    "Keine Spalte für die Rolle. Alle werden Teilnehmende, sofern Sie es nicht ändern.",
  "import.note.groupColumnAbsent": "Keine Spalte für die Gruppe. Niemand wird einer Gruppe zugeordnet.",
  "import.note.ageColumnAbsent": "Keine Spalte für Alter oder Jahrgang.",
  "import.note.aiUnavailableUsedHeuristic":
    "Der Schreibassistent war nicht erreichbar; die Spalten wurden über ihre Namen zugeordnet.",
  "import.note.lowConfidence": "Die Spalten waren schwer zu lesen. Bitte jede einzeln prüfen.",
  "import.review.title": "Jede Person, bevor irgendetwas angelegt wird",
  "import.review.intro":
    "Noch existiert nichts. Korrigieren Sie, was falsch ist, schließen Sie aus, wer nicht dazugehört — und legen Sie die Konten erst dann an.",
  "import.review.summary": "{ready} bereit · {attention} brauchen Aufmerksamkeit",
  "import.review.unreadable": "{count} Zeilen waren gar nicht lesbar und stehen unten nicht",
  "import.review.excludedCount": "{count} ausgeschlossen",
  "import.review.column.row": "Zeile",
  "import.review.column.name": "Name",
  "import.review.column.role": "Rolle",
  "import.review.column.group": "Gruppe",
  "import.review.column.ageBand": "Altersstufe",
  "import.review.column.problems": "Probleme",
  "import.review.exclude": "Ausschließen",
  "import.review.include": "Wieder aufnehmen",
  "import.review.commit": "{count} Konten anlegen",
  "import.review.blocked": "Bitte alles rot Markierte beheben, bevor Konten angelegt werden.",
  "import.review.back": "Zurück zu den Spalten",
  "import.problem.noName": "Kein Name",
  "import.problem.nameTooLong": "Länger als {max} Zeichen",
  "import.problem.groupNameTooLong": "Gruppenname länger als {max} Zeichen",
  "import.problem.invalidAgeBand": "Keine Altersstufe, die wir verwenden",
  "import.problem.ageBandRequired": "{group} existiert noch nicht, und eine neue Gruppe braucht eine Altersstufe",
  "import.problem.duplicateInGroup": "{count} Personen mit diesem Namen in dieser Gruppe",
  "import.ageBand.u13": "Unter 13",
  "import.ageBand.13-15": "13 bis 15",
  "import.ageBand.16-17": "16 bis 17",
  "import.ageBand.adult": "18 und älter",
  "import.ageBand.mixed": "Gemischt",
  "import.ageBand.none": "Nicht gesetzt",
  "import.credentials.title": "{count} Personen hinzugefügt",
  "import.credentials.skipped": "{count} Zeilen wurden nicht importiert",
  "import.credentials.groupsCreated.one": "{count} neue Gruppe wurde angelegt",
  "import.credentials.groupsCreated.other": "{count} neue Gruppen wurden angelegt",
  "import.credentials.warning":
    "Passwörter werden einmal angezeigt und lassen sich nicht wieder abrufen. Drucken Sie die Blätter jetzt. Gehen sie verloren, vergeben Sie neue — nachschlagen kann sie niemand, auch wir nicht.",
  "import.credentials.noneNeeded": "Nichts zu drucken.",
  "import.credentials.noneNeededWhy": "Alle in diesem Import sind Teilnehmende. Sie halten ein von der Einrichtung eingetragenes Gerät und melden sich nie an, es gibt also keine Passwörter zu verteilen.",
  "import.credentials.staffOnly": "{count} Zettel, nur für Personal. Teilnehmende halten ein eingetragenes Gerät und haben kein Passwort.",
  "import.credentials.print": "Zugangsblätter drucken",
  "import.credentials.printing": "Blätter werden erstellt …",
  "import.credentials.opened": "Die Blätter wurden in einem neuen Tab geöffnet.",
  "import.credentials.reprint":
    "Ein erneuter Druck vergibt neue Passwörter; die auf dem alten Blatt gelten dann nicht mehr.",
  "import.credentials.done": "Fertig",
  "import.skip.noName": "Kein Name in der Zeile",
  "import.skip.nameTooLong": "Der Name war zu lang",
  "import.skip.invalidRole": "Die Rolle war nicht lesbar",
  "import.skip.groupNameTooLong": "Der Gruppenname war zu lang",
  "import.skip.invalidAgeBand": "Die Altersstufe war nicht lesbar",

  // ── Gruppen ───────────────────────────────────────────────────────
  "groups.title": "Gruppen",
  "groups.new": "Neue Gruppe",
  "groups.showArchived": "Archivierte anzeigen",
  "groups.empty": "Noch keine Gruppen",
  "groups.emptyBody":
    "Eine Gruppe ist eine Klasse oder ein Kurs: ihre Lehrkräfte und die Teilnehmenden auf der Liste. Jede Stunde wird mit einer gehalten. Legen Sie die erste hier an oder importieren Sie unter Personen eine Klassenliste.",
  "groups.column.name": "Name",
  "groups.column.ageBand": "Altersstufe",
  "groups.column.teachers": "Lehrkräfte",
  "groups.column.students": "Teilnehmende",
  "groups.column.lastSession": "Letzte Stunde",
  "groups.column.mode": "Wo die Stunde stattfindet",
  "groups.archived": "Archiviert",
  "groups.noSessions": "Noch keine",
  "groups.studentCount": "{count}",

  "group.new.title": "Neue Gruppe",
  "group.name": "Name",
  "group.nameHint": "Wie die Lehrkraft sie nennt. Klasse 8b, Dienstagabend, Ensemble 2.",
  "group.ageBand": "Altersstufe",
  "group.ageBandMinorsWarning":
    "In dieser Gruppe sind Personen unter 18. Vor der ersten Stunde muss die Einwilligung der Eltern hinterlegt sein. Sie erfassen sie; wir treten nie an eine Familie heran.",
  "group.ageBandAdults": "Niemand unter 18 — eine Einwilligung der Eltern ist nicht nötig.",
  "group.ageBandMinorsNoConsent":
    "In dieser Gruppe sind Personen unter 18; ihre Daten werden nach den Regeln für Jugendliche behandelt. Eine Einwilligung der Eltern ist in diesem Alter bei Ihnen nicht erforderlich.",
  "group.defaultMode": "Wo die Stunde stattfindet",
  "group.defaultModeHint": "Das entscheidet, ob die Stunde das WLAN des Kits braucht.",
  "group.mode.online": "Alle zu Hause",
  "group.mode.offline": "Alle in einem Raum (mit Kit)",
  "group.teachers": "Lehrkräfte",
  "group.teachersHint":
    "Beliebig viele, auch Sie selbst. Eine davon leitet jede Stunde.",
  "group.roster": "Teilnehmende",
  "group.rosterHint": "Jetzt optional. Sie können später ergänzen oder eine Liste importieren.",
  "group.create": "Gruppe anlegen",
  "group.create.needsName": "Geben Sie der Gruppe zuerst einen Namen.",
  "group.create.needsTeacher":
    "Wählen Sie mindestens eine Lehrkraft — ohne Lehrkraft lässt sich keine Stunde halten.",
  "group.created": "{name} angelegt.",

  "group.tab.roster": "Liste",
  "group.tab.teachers": "Lehrkräfte",
  "group.tab.settings": "Einstellungen",
  "group.tab.devices": "Geräte",
  "group.tab.history": "Stunden",

  "group.roster.add": "Teilnehmende hinzufügen",
  "group.roster.remove": "Entfernen",
  "group.roster.removeTitle": "{name} aus {group} entfernen",
  "group.roster.removeBody":
    "Die Geräte für diese Gruppe werden gesperrt; ohne erneute Registrierung ist keine Teilnahme mehr möglich. Konto und alles Übrige bleiben unverändert.",
  "group.roster.removeConfirm": "Zur Bestätigung {name} eingeben",
  "group.roster.removed": "{name} entfernt.",
  "group.roster.empty": "Noch niemand auf der Liste",
  "group.roster.addedOn": "Hinzugefügt am {date}",
  "group.roster.added": "{count} hinzugefügt.",
  "group.roster.chooseStudents": "Welche Teilnehmenden?",
  "group.roster.noneToAdd": "Alle stehen bereits auf dieser Liste.",

  "group.teachers.add": "Lehrkraft hinzufügen",
  "group.teachers.remove": "Entfernen",
  "group.teachers.lastOne":
    "Das ist die einzige Lehrkraft der Gruppe. Ohne Lehrkraft lässt sich keine Stunde halten — fügen Sie zuerst eine weitere hinzu.",
  "group.teachers.chooseTeacher": "Welche Lehrkraft?",
  "group.teachers.noneToAdd": "Alle Lehrkräfte gehören bereits zu dieser Gruppe.",

  "group.settings.archive": "Gruppe archivieren",
  "group.settings.unarchive": "Aus dem Archiv holen",
  "group.settings.archiveBody":
    "Sie verschwindet aus der Liste und aus der App. Gelöscht wird nichts, und Sie können sie jederzeit zurückholen.",
  "group.settings.assessmentOverride": "Bewertungsrahmen für diese Gruppe",
  "group.settings.assessmentInherit": "Wie in der Einrichtung",
  "group.settings.assessmentHint":
    "Die Skala, nach der Sie ohnehin bewerten. Lassen Sie es bei der Einrichtung, solange diese Gruppe nicht anders bewertet wird.",

  "group.devices.empty": "Für niemanden auf dieser Liste ist ein Gerät registriert",
  "group.devices.stale": "Seit {days} Tagen nicht benutzt",
  "group.devices.column.student": "Teilnehmende:r",
  "group.devices.column.label": "Gerät",
  "group.devices.column.enrolled": "Registriert",
  "group.devices.column.lastSeen": "Zuletzt gesehen",

  "group.history.empty": "Mit dieser Gruppe wurde noch keine Stunde gehalten",
  "group.history.column.date": "Datum",
  "group.history.column.teacher": "Lehrkraft",
  "group.history.column.mode": "Verbindung",
  "group.history.column.attendance": "Anwesend",
  "group.history.live": "Läuft gerade",

  "group.consent.title": "Einwilligung der Eltern",
  "group.consent.coverage": "{recorded} von {total} erfasst",
  "group.consent.complete": "Für alle auf dieser Liste liegt eine Einwilligung vor.",
  "group.consent.missing": "Fehlt für {names}",
  "group.consent.record": "Einwilligung erfassen",
  "group.consent.blocks": "Bis alle vorliegen, lässt sich keine Stunde starten.",

  // ── Lernziele ─────────────────────────────────────────────────────
  "goal.status.title": "Status",
  "goal.status.description":
    "Wer steht über wem — und was passiert, wenn sich das dreht. Das Rückgrat der meisten Szenen, die funktionieren.",
  "goal.accepting_offers.title": "Angebote annehmen",
  "goal.accepting_offers.description":
    "Nehmen, was das Gegenüber anbietet, und darauf aufbauen, statt zu blockieren.",
  "goal.emotional_range.title": "Emotionale Bandbreite",
  "goal.emotional_range.description":
    "Weiter weg von der eigenen Tonlage spielen, als bequem ist — und dort wahrhaftig bleiben.",
  "goal.physicality.title": "Körperlichkeit",
  "goal.physicality.description":
    "Zuerst der Körper. Was eine Person tut, bevor sie etwas sagt — und statt etwas zu sagen.",
  "goal.listening.title": "Zuhören",
  "goal.listening.description":
    "Sich von dem verändern lassen, was man hört. Der Punkt, der alles andere möglich macht.",
  "goal.text_work.title": "Textarbeit",
  "goal.text_work.description":
    "Arbeit mit einem Skript: Timing, auf das Stichwort hören und die Absicht unter den Worten.",
  "goal.source": "Nach {name}",

  // ── Szenenprüfung (Schlüssel des Backends) ──────────────────────
  "scenario.error.roleCount": "Eine Szene braucht zwischen {min} und {max} Rollen.",
  "scenario.error.duplicateRoleId": "Zwei Rollen haben dieselbe Kennung.",
  "scenario.error.roleUnused": "{role} hat nichts zu tun.",
  "scenario.error.roleNameEmpty": "Eine Rolle braucht einen Namen.",
  "scenario.error.tooFewBeats": "Eine Szene braucht mindestens {min} Beats.",
  "scenario.error.cueOrder": "Die Beats sind nicht in der richtigen Reihenfolge.",
  "scenario.error.cueUnknownRole": "Dieser Beat gehört zu einer Rolle, die es in der Szene nicht gibt.",
  "scenario.error.emotionEmpty": "Ein Beat braucht ein Gefühl.",
  "scenario.error.duplicateCueId": "Zwei Beats haben dieselbe Kennung.",
  "scenario.error.openingLength": "Der Anfang muss zwischen 1 und {max} Zeichen lang sein.",
  "scenario.error.castRange": "Die Besetzungsspanne passt nicht zu den Rollen der Szene.",
  "scenario.error.unknownSource": "Diese Schule zitieren wir nicht.",
  "scenario.error.constraintLabelEmpty": "Die wiederkehrende Regel braucht einen Namen.",
  "scenario.error.consecutiveSameRole":
    "Zwei Beats hintereinander für dieselbe Person. Fassen Sie sie zusammen.",
  "scenario.error.situationTooLong": "Eine Situation hat höchstens {max} Zeichen.",
  "scenario.error.noCues": "Eine Szene braucht mindestens einen Beat.",
  "scenario.error.tooFewRoles": "Eine Szene braucht mindestens zwei Rollen.",
  "scenario.error.roleWithoutCues": "{role} hat nichts zu tun.",
  "scenario.error.cueWithoutRole": "Ein Beat zeigt auf eine Rolle, die es in der Szene nicht gibt.",
  "scenario.error.castRangeInvalid": "Die kleinste Besetzung kann nicht größer sein als die größte.",
  "scenario.error.openingEmpty": "Es gibt nichts, was die Lehrkraft vorlesen könnte.",
  "scenario.error.openingTooLong": "Der Anfang hat höchstens {max} Zeichen.",
  "scenario.error.titleEmpty": "Eine Szene braucht einen Titel.",
  "scenario.error.iconUnknown": "Dieses Symbol kann die Karte nicht anzeigen. Wählen Sie eines aus der Liste.",
  "scenario.error.lineEmpty": "Ein Satz braucht seinen Text.",
  "scenario.error.lineTooLong": "Ein Satz hat höchstens 400 Zeichen. Teilen Sie ihn auf zwei Beats auf.",
  "scenario.error.imageMissing": "Dieser Beat hat noch kein Bild.",
  "scenario.error.captionTooLong": "Eine Bildunterschrift hat höchstens 80 Zeichen.",
  "scenario.error.imageOnTextBeat": "Nur ein Bild-Beat kann ein Bild tragen.",

  // ── Stunden ───────────────────────────────────────────────────────
  "sessions.title": "Stunden",
  "sessions.empty": "Es wurde noch keine Stunde gehalten",
  "sessions.emptyBody":
    "Stunden werden mit der Lehrkraft-App auf einem Tablet gehalten, nicht hier. Was in einer geschehen ist, sehen Sie hier im Nachhinein.",
  "sessions.noMatch": "Zu diesen Filtern gibt es keine Stunde",
  "sessions.noMatchBody": "Versuchen Sie „Alle“ bei der Gruppe oder „Alle“ bei der Verbindung.",
  "sessions.clearFilters": "Alle Stunden zeigen",
  "sessions.column.date": "Datum",
  "sessions.column.group": "Gruppe",
  "sessions.column.teacher": "Lehrkraft",
  "sessions.column.runs": "Szenen",
  "sessions.column.attendance": "Anwesend",
  "sessions.column.duration": "Dauer",
  "sessions.column.mode": "Verbindung",
  "sessions.filter.group": "Gruppe",
  "sessions.filter.teacher": "Lehrkraft",
  "sessions.filter.mode": "Verbindung",
  "sessions.minutes": "{count} Min.",
  "sessions.attendance": "{present} von {total}",
  "sessions.live": "Läuft gerade",
  "sessions.ownerNotice":
    "Sie sehen, dass eine Stunde stattgefunden hat, und ihre Statistik. Was darin passiert ist — wer welche Rolle gespielt hat, was die Lehrkraft notiert hat — gehört der Lehrkraft, die sie gehalten hat.",

  "review.title": "{group}, {date}",
  "review.tab.overview": "Überblick",
  "review.tab.runs": "Szenen",
  "review.tab.scores": "Bewertungen",
  "review.tab.notes": "Notizen",
  "review.register": "Wer da war",
  "review.present": "Anwesend",
  "review.absent": "Abwesend",
  "review.run": "Szene {number}",
  "review.runScenario": "{title}, Fassung {version}",
  "review.runDuration": "{count} Min.",
  "review.runAborted": "Vorzeitig beendet",
  "review.cast": "Wer was gespielt hat",
  "review.castAs": "als {role}",
  "review.difficulty": "Schwierigkeit {level}",
  "review.timeline": "Beat für Beat",
  "review.liveScene": "Live-Szene",
  "review.beat.line": "„{text}“",
  "review.beat.picture": "Bild",
  "review.beat.pictureMissing": "Bild nicht verfügbar",
  "review.noRuns": "In dieser Stunde wurde keine Szene gespielt",

  "review.scores.intro":
    "Optional — und bleibt es. Eine Stunde ohne Bewertungen ist eine ganz normale Stunde.",
  "review.scores.framework": "Bewertet nach {name}",
  "review.scores.none": "Nicht bewertet",
  "review.scores.clear": "Entfernen",
  "review.scores.saved": "Erfasst.",

  "review.notes.label": "Was Sie sich merken wollen",
  "review.notes.retention":
    "Nur für Sie, und bis zum {date} aufbewahrt. Danach wird es gelöscht, zusammen mit allem anderen zum Ablauf dieser Stunde. Statistiken bleiben.",
  "review.notes.saving": "Wird gespeichert …",
  "review.notes.saved": "Gespeichert",
  "review.retentionSoon":
    "Die Details dieser Stunde werden am {date} gelöscht. Statistiken bleiben erhalten.",
  "review.retentionGone":
    "Die Details dieser Stunde wurden gelöscht. Geblieben ist die Statistik.",
  "review.teacherOnly":
    "Die Details einer Stunde öffnet nur die Lehrkraft, die sie gehalten hat.",
  "review.notFound": "Diese Stunde ist nicht hier",
  "review.notFoundBody":
    "Der Link kann alt sein, oder die Stunde ist aus der Aufbewahrungsfrist gefallen.",
  "review.allLessons": "Alle Stunden",

  // ── das Protokoll des Raums (Iteration 2) ─────────────────────────
  "sessions.kind.room": "Raumstunde",
  "sessions.kind.show": "Showabend",
  "review.lesson": "Stunde",
  "review.tab.room": "Raum",
  "review.tab.show": "Show",
  "review.dries.title": "Hänger-Karte",
  "review.dries.intro":
    "Wo jede Person ihren Text brauchte: von Ihnen souffliert oder von der Karte aus erfragt. Ein Beat zählt einmal, egal wie oft souffliert wurde. Eine Information, keine Bewertung.",
  "review.dries.summary": "{count} Hänger · {prompted} souffliert · {asked} erfragt",
  "review.dries.none": "Keine Hänger",
  "review.dries.beat": "Beat {number}",
  "review.dries.offScript": "Ein Beat, der nicht mehr in der Szene ist",
  "review.dries.prompted": "Souffliert: {text}",
  "review.dries.promptedLine": "Souffliert",
  "review.dries.asked": "Nach dem Text gefragt",
  "review.dries.marks": "Markierungen je Beat",
  "review.dries.mark.clean": "Beat {number}, {role}: kein Hänger",
  "review.dries.mark.prompted": "Beat {number}, {role}: Hänger, souffliert",
  "review.dries.mark.asked": "Beat {number}, {role}: Hänger, nach dem Text gefragt",
  "review.dries.mark.both": "Beat {number}, {role}: Hänger, nach dem Text gefragt und souffliert",
  "review.dries.legend": "● souffliert · ○ erfragt",
  "review.rounds.title": "Runden",
  "review.rounds.intro": "Jede Runde, die der Raum gespielt hat: die Übung, wer dran war und wie lange.",
  "review.rounds.round": "Runde {number}",
  "review.rounds.column.exercise": "Übung",
  "review.rounds.column.who": "Wer dran war",
  "review.rounds.column.length": "Dauer",
  "review.rounds.untitled": "Runde ohne Namen",
  "review.rounds.empty": "In dieser Stunde wurde keine Runde gespielt",
  "review.matrix.title": "Wer mit wem gearbeitet hat",
  "review.matrix.intro":
    "Wie oft jedes Paar zusammen dran war und wie oft jede Person überhaupt.",
  "review.matrix.timesUp": "Wie oft dran",
  "review.matrix.cell": "{a} mit {b}: {count}",
  "review.acts.title": "Auftritte",
  "review.acts.intro": "Die Reihenfolge, wie sie gespielt wurde.",
  "review.acts.act": "Auftritt {number}",
  "review.acts.column.title": "Auftritt",
  "review.acts.column.performers": "Auf der Bühne",
  "review.acts.column.length": "Dauer",
  "review.acts.untitled": "Auftritt ohne Titel",
  "review.acts.empty": "In dieser Stunde wurde kein Auftritt gespielt",
  "review.jam.title": "Jam-Auslosungen",
  "review.jam.draw": "Auslosung {number}",
  "review.jam.group": "Gruppe {number}",
  "review.jam.empty": "Es wurden keine Namen gezogen",
  "group.history.dries.title": "Textsicherheit über die Stunden",
  "group.history.dries.intro":
    "Hänger je Textszene, älteste Stunde zuerst – nur Stunden, die Sie gehalten haben. Eine Information, keine Bewertung.",
  "group.history.dries.entry": "{date}: {count} Hänger bei {beats} Beats",

  // ── Bewertungsrahmen ──────────────────────────────────────────────
  "assessment.epa.name": "EPA / KMK",
  "assessment.epa.level1": "Reproduktion — gibt wieder, was gezeigt wurde",
  "assessment.epa.level2": "Reorganisation — verbindet Bekanntes auf neue Weise",
  "assessment.epa.level3": "Transfer — überträgt es auf eine unbekannte Situation",
  "assessment.epa.level4": "Reflexion — beurteilt die eigenen Entscheidungen und begründet sie",
  "assessment.aoao.name": "AO1–AO4",
  "assessment.aoao.level1": "AO1 — entwickelt eigene Ideen",
  "assessment.aoao.level2": "AO2 — setzt theatrale Mittel ein",
  "assessment.aoao.level3": "AO3 — zeigt Kenntnis der Praxis",
  "assessment.aoao.level4": "AO4 — analysiert und bewertet die eigene Arbeit",
  "assessment.ncas.name": "NCAS",
  "assessment.ncas.level1": "Creating — erfindet und entwirft",
  "assessment.ncas.level2": "Performing — bringt es zur Aufführung",
  "assessment.ncas.level3": "Responding — nimmt wahr und deutet",
  "assessment.ncas.level4": "Connecting — stellt Bezüge zu Kontext und Erfahrung her",
  "assessment.custom.name": "Eigener",
  "assessment.custom.level1": "1",
  "assessment.custom.level2": "2",
  "assessment.custom.level3": "3",
  "assessment.custom.level4": "4",

  // ── Auswertungen ──────────────────────────────────────────────────
  "reports.title": "Auswertungen",
  "reports.noStudentData":
    "Nur aus Statistiken gebaut. Nichts hier identifiziert eine teilnehmende Person — und nichts hier kann es.",
  "reports.range.month": "Dieser Monat",
  "reports.range.term": "Dieses Halbjahr",
  "reports.range.year": "Dieses Jahr",
  "reports.range.custom": "Zeitraum wählen",
  "reports.range.from": "Von",
  "reports.range.to": "Bis",
  "reports.export": "Als CSV herunterladen",
  "reports.exported": "Heruntergeladen.",
  "reports.empty": "In diesem Zeitraum ist nichts passiert",

  "reports.usage.title": "Nutzung",
  "reports.usage.sessions": "Stunden",
  "reports.usage.runs": "Szenen",
  "reports.usage.groups": "Gruppen, die sich getroffen haben",
  "reports.usage.teachers": "Lehrkräfte, die unterrichtet haben",
  "reports.usage.minutes": "Unterrichtete Minuten",
  "reports.usage.attendance": "Durchschnittliche Anwesenheit",
  "reports.usage.attendanceRate": "Durchschnittliche Beteiligung",
  "reports.usage.online": "Online",
  "reports.usage.offline": "Offline",
  "reports.usage.seats": "Belegte Lehrkraft-Plätze",
  "reports.usage.seatsOf": "{used} von {total}",

  "reports.content.title": "Welche Szenen funktioniert haben",
  "reports.content.intro":
    "Eine Szene, zu der eine Lehrkraft zurückkehrt, ist eine Szene, die funktioniert hat. Genau das ist die Wiederholungsrate.",
  "reports.content.column.scenario": "Szene",
  "reports.content.column.uses": "Wie oft eingesetzt",
  "reports.content.column.repeatRate": "Wieder verwendet",
  "reports.content.column.groups": "Gruppen",
  "reports.content.column.teachers": "Lehrkräfte",
  "reports.content.column.minutes": "Durchschnittliche Dauer",

  "reports.activity.title": "Im Zeitverlauf",
  "reports.activity.column.month": "Monat",
  "reports.activity.column.sessions": "Stunden",
  "reports.activity.column.runs": "Szenen",
  "reports.activity.column.groups": "Gruppen",
  "reports.activity.column.minutes": "Minuten",

  "reports.teachers.title": "Aktivität der Lehrkräfte",
  "reports.teachers.usageNotPerformance":
    "Nutzung, nicht Leistung. Das ist keine Rangliste und wird bewusst auch nicht als eine angeboten — wie viel jemand ein Werkzeug benutzt hat, sagt nichts darüber, wie gut unterrichtet wurde.",
  "reports.teachers.column.teacher": "Lehrkraft",
  "reports.teachers.column.sessions": "Stunden",
  "reports.teachers.column.runs": "Szenen",
  "reports.teachers.column.groups": "Gruppen",
  "reports.teachers.column.minutes": "Minuten",

  "reports.summary.title": "In einem Absatz",
  "reports.summary.generate": "Zusammenfassung schreiben",
  "reports.summary.writing": "Wird geschrieben …",
  "reports.summary.label":
    "Vom Assistenten aus den Zahlen oben geschrieben. Er sieht nie einen Namen — weder von Teilnehmenden noch von Lehrkräften.",
  "reports.teacherScope":
    "Ihre eigenen Gruppen. Der Blick auf die ganze Einrichtung gehört der Leitung.",

  // ── Abrechnung ────────────────────────────────────────────────────
  "billing.title": "Abrechnung",
  "billing.plan.title": "Ihre Lizenz",
  "billing.plan.tier": "Tarif",
  "billing.plan.seats": "Lehrkräfte",
  "billing.plan.seatsOf": "{used} von {total}",
  "billing.plan.renews": "Läuft bis",
  "billing.plan.status": "Status",
  "billing.plan.cancelAtPeriodEnd": "Endet zum Ende dieser Periode",
  "billing.tier.solo": "Solo",
  "billing.tier.small": "Klein",
  "billing.tier.medium": "Mittel",
  "billing.tier.large": "Groß",
  "billing.tier.teachers": "Bis zu {count} Lehrkräfte",
  "billing.tier.teachersOne": "Eine Lehrkraft",
  "billing.tier.current": "Ihr Tarif",
  "billing.tier.choose": "Zu {tier} wechseln",
  "billing.tier.blocked":
    "Sie haben {used} Lehrkräfte; {tier} erlaubt {max}. Entfernen Sie zuerst einige, dann ist der Wechsel möglich.",
  "billing.tier.priceElsewhere":
    "Die Preise sehen Sie beim Bezahlen. Sie liegen in Stripe — eine Preisänderung ist damit nie eine Änderung an dieser Anwendung.",

  "billing.portal.title": "Zahlungsmittel und Rechnungen",
  "billing.portal.body":
    "Karten, Rechnungen und Belege verwaltet Stripe. Wir sehen nie eine Kartennummer.",
  "billing.portal.open": "Abrechnungsportal öffnen",

  "billing.state.expiring.title": "Ihre Lizenz läuft am {date} aus",
  "billing.state.expiring.body":
    "Verlängern Sie vorher, dann ändert sich nichts. Gelöscht wird ohnehin nichts — Szenen, Gruppen und Statistiken bleiben genau so, wie sie sind.",
  "billing.state.paymentFailed.title": "Eine Zahlung ist nicht durchgegangen",
  "billing.state.paymentFailed.body":
    "Die Stunden laufen weiter. Stripe versucht es in den nächsten Tagen erneut; Sie können die Karte auch jetzt im Abrechnungsportal ändern.",
  "billing.state.expired.title": "Ihre Lizenz ist abgelaufen",
  "billing.state.expired.body":
    "Neue Stunden lassen sich nicht starten. Eine laufende Stunde bleibt unberührt, und gelöscht wird nichts — jede Szene, Gruppe, Auswertung und Statistik gehört weiter Ihnen und lässt sich exportieren.",
  "billing.state.manual.title": "Abrechnung per Rechnung",
  "billing.state.manual.body":
    "Ihre Lizenz ist vereinbart statt abonniert; hier gibt es deshalb nichts einzustellen. Schreiben Sie uns, wir regeln das.",

  "billing.checkout.opening": "Bezahlvorgang wird geöffnet …",
  "billing.checkout.legalFirst":
    "Vor dem Kauf einer Lizenz sind noch Dokumente anzunehmen. Der Auftragsverarbeitungsvertrag gehört zu dem, was Sie vereinbaren — nicht als Nachtrag.",
  "billing.checkout.toLegal": "Ansehen",
  "billing.checkout.waiting":
    "Die Zahlung ist durch. Wir warten auf die Bestätigung von Stripe — das dauert meist wenige Sekunden, und es geht nichts verloren, wenn Sie die Seite verlassen.",
  "billing.checkout.cancelled": "Es wurde nichts berechnet und nichts geändert.",

  "billing.seatLimitInline":
    "Ihre Lizenz {tier} umfasst {max} Lehrkräfte, {used} sind belegt. Ein größerer Tarif ist eine Minute entfernt.",
  "billing.seatLimitAction": "Tarife ansehen",

  "nav.support": "Supportzugriff",
  "support.title": "Supportzugriff",
  "support.intro":
    "Spontar kann die Inhalte Ihrer Einrichtung nicht aus eigener Machtvollkommenheit ansehen. Wer dort etwas braucht, fragt Sie hier — Sie entscheiden, der Zugriff ist nur lesend, und er endet von selbst.",
  "support.empty": "Niemand bei Spontar hat um Einsicht gebeten",
  "support.column.reason": "Wofür es angeblich gebraucht wird",
  "support.column.asked": "Angefragt",
  "support.column.who": "Wer",
  "support.column.state": "Status",
  "support.column.remaining": "Rest",
  "support.approve": "Einlassen",
  "support.deny": "Ablehnen",
  "support.revoke": "Jetzt beenden",
  "support.approved": "Bis zum Ende des Zeitfensters ist Einsicht möglich.",
  "support.denied": "Abgelehnt. Die Anfrage wurde beantwortet.",
  "support.revoked": "Beendet.",
  "support.duration.1": "eine Stunde",
  "support.duration.8": "einen Arbeitstag",
  "support.duration.24": "einen Tag",
  "support.forHours": "Für {duration}",
  "support.remaining": "Noch {count} Minuten",
  "support.readOnly":
    "Immer nur lesend. Niemand bei Spontar kann in Ihrer Einrichtung etwas ändern, und die Notizen und Bewertungen einer Lehrkraft sind nie dabei.",

  "support.state.pending": "Wartet auf Sie",
  "support.state.approved": "Offen",
  "support.state.denied": "Von Ihnen abgelehnt",
  "support.state.revoked": "Von Ihnen beendet",
  "support.state.expired": "Beendet",

  "support.banner.active":
    "{who} bei Spontar sieht sich Ihre Einrichtung noch {minutes} Minuten lang an, und zwar weil: {reason}",
  "support.banner.readOnly": "Nur lesend. Es kann nichts geändert werden.",
  "support.banner.close": "Jetzt beenden",
  "support.banner.pending":
    "Jemand bei Spontar hat um Einsicht in Ihre Einrichtung gebeten. Bis Sie entscheiden, ist nichts offen.",
  "support.banner.decide": "Entscheiden",

  "support.views.title": "Was angesehen wurde",
  "support.views.empty": "Noch nichts",
  "support.views.column.view": "Seite",
  "support.views.column.at": "Wann",

  "support.error.requestNotFound": "Diese Anfrage wurde nicht gefunden.",
  "support.error.alreadyDecided": "Diese Anfrage ist bereits beantwortet.",
  "support.error.notApproved": "Dieser Zugriff ist nicht offen.",
  "support.error.notActive": "Dieser Zugriff ist beendet.",
};
